import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    User,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
    currentUser: User | null;
    login: (email: string, password: string) => Promise<any>;
    signup: (email: string, password: string) => Promise<any>;
    logout: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

function mapFirebaseAuthError(error: any): string {
    if (!error || typeof error !== 'object') return 'An unknown error occurred. Please try again.';
    const code = error.code || error.message || '';
    if (code.includes('auth/invalid-email')) return 'Please enter a valid email address.';
    if (code.includes('auth/user-not-found')) return 'No account found with this email.';
    if (code.includes('auth/wrong-password')) return 'Incorrect password. Please try again.';
    if (code.includes('auth/email-already-in-use')) return 'This email is already registered.';
    if (code.includes('auth/weak-password')) return 'Password should be at least 6 characters.';
    if (code.includes('auth/too-many-requests')) return 'Too many attempts. Please try again later.';
    if (code.includes('auth/network-request-failed')) return 'Network error. Please check your connection.';
    return 'Authentication failed. Please try again.';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    function signup(email: string, password: string) {
        return createUserWithEmailAndPassword(auth, email, password).catch((error) => {
            throw new Error(mapFirebaseAuthError(error));
        });
    }

    function login(email: string, password: string) {
        return signInWithEmailAndPassword(auth, email, password).catch((error) => {
            throw new Error(mapFirebaseAuthError(error));
        });
    }

    function logout() {
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            console.log('Auth state changed:', user);
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        login,
        signup,
        logout,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
} 